// src/components/ServiceForm.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Error403Page from "./Error_backend";
import DeleteModal from "./DeleteModal";
import api from "../api.js";
import "../style/css/AppointmentForm.css";

interface ServiceType {
  id: string;
  name: string;
}

interface ServiceFormData {
  name: string;
  description: string;
  service_type: string; // Will be empty string if no service type
  duration_minutes: string;
  price: string;
  status: boolean;
}

const ServiceForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<ServiceFormData>({
    name: "",
    description: "",
    service_type: "", // Default to empty string
    duration_minutes: "",
    price: "",
    status: true,
  });

  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [loadingServiceTypes, setLoadingServiceTypes] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // API base URL
  const API_BASE = "/services";
  const SERVICE_TYPES_API = "/service-types/";

  // Validate form data
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields validation
    if (!formData.name.trim()) {
      errors.name = "El nombre del servicio es requerido";
    }

    // Service type is now optional - remove required validation

    if (!formData.duration_minutes.trim()) {
      errors.duration_minutes = "La duración es requerida";
    } else {
      const duration = parseInt(formData.duration_minutes);
      if (isNaN(duration) || duration <= 0 || duration > 480) {
        errors.duration_minutes =
          "La duración debe ser un número válido entre 1 y 480 minutos";
      }
    }

    if (!formData.price.trim()) {
      errors.price = "El precio es requerido";
    } else {
      const price = parseFloat(formData.price);
      if (isNaN(price) || price < 0) {
        errors.price = "El precio debe ser un número válido mayor o igual a 0";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setError(null);

    try {
      const response = await api.delete(`${API_BASE}/${id}/`);

      if (!response) {
        throw new Error("Error al eliminar servicio");
      }

      navigate("/services");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ocurrió un error");
    } finally {
      setDeleting(false);
    }
  };

  // Fetch service types
  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        setLoadingServiceTypes(true);
        const response = await api.get(SERVICE_TYPES_API);
        if (response && response.data) {
          setServiceTypes(response.data);
        }
      } catch (err) {
        // Don't set error here - service types are optional
      } finally {
        setLoadingServiceTypes(false);
      }
    };

    fetchServiceTypes();
  }, []);

  // Fetch service data if in edit mode
  useEffect(() => {
    const fetchService = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`${API_BASE}/${id}/`);
        if (!response) {
          throw new Error("Failed to fetch service");
        }

        const serviceData = await response.data;

        // Format the data for the form - handle service_type safely
        setFormData({
          name: serviceData.name || "",
          description: serviceData.description || "",
          service_type: serviceData.service_type?.id || "", // Use optional chaining
          duration_minutes: serviceData.duration_minutes?.toString() || "",
          price: serviceData.price?.toString() || "",
          status: serviceData.status || false,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;

    const newValue =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setError("Por favor, corrija los errores en el formulario");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Prepare data for API - convert numeric fields and handle optional service_type
      const dataToSave = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        service_type_id: formData.service_type || null, // Send null if empty string
        duration_minutes: parseInt(formData.duration_minutes),
        price: parseFloat(formData.price),
        status: formData.status,
      };

      let response;
      if (isEditMode) {
        response = await api.put(`${API_BASE}/${id}/`, dataToSave);
      } else {
        response = await api.post(`${API_BASE}/`, dataToSave);
      }

      if (!response) {
        throw new Error(
          `Failed to ${isEditMode ? "update" : "create"} service`,
        );
      }

      navigate("/services");
    } catch (err: any) {
      // Handle API errors more gracefully
      if (err.response?.data) {
        // Backend validation errors
        const backendErrors = err.response.data;
        let errorMessage = "Error al guardar el servicio";

        // You can map backend errors to form errors here if needed
        if (typeof backendErrors === "object") {
          const firstError = Object.values(backendErrors)[0];
          errorMessage = Array.isArray(firstError)
            ? firstError[0]
            : String(firstError);
        }

        setError(errorMessage);
      } else {
        setError(err instanceof Error ? err.message : "An error occurred");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBackClick = () => {
    navigate("/services");
  };

  // Format duration for display
  const formatDuration = (minutes: string): string => {
    const mins = parseInt(minutes);
    if (isNaN(mins)) return "";

    if (mins < 60) {
      return `${mins} min`;
    } else {
      const hours = Math.floor(mins / 60);
      const remainingMins = mins % 60;
      return remainingMins > 0 ? `${hours}h ${remainingMins}min` : `${hours}h`;
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="form-entire-container-form-appointment">
        <div className="form-container-form-appointment">
          <div className="loading-message-form-appointment">
            <i className="fas fa-spinner fa-spin"></i> Cargando servicio...
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && isEditMode && loading) {
    return (
      <Error403Page
        code={500}
        title="Recurso no disponible."
        message="Hemos tenido problemas para acceder al recurso."
        homeHref="/"
        loginHref="/login"
        supportHref="/support"
        onHomeClick={(e) => {
          e.preventDefault();
          navigate("/");
        }}
        onLoginClick={(e) => {
          e.preventDefault();
          navigate("/login");
        }}
      />
    );
  }

  const titleText = isEditMode ? "Editar Servicio" : "Crear Nuevo Servicio";

  return (
    <div className="form-entire-container-form-appointment">
      <div className="form-container-form-appointment">
        <div className="header-actions-form-appointment">
          <button
            className="back-link-form-appointment"
            onClick={handleBackClick}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <i className="fas fa-arrow-left"></i> Volver a Servicios
          </button>
          <h2 style={{ margin: 0, fontSize: "1.5rem" }}>
            <i
              className="fas fa-concierge-bell"
              style={{ color: "var(--color-accent)" }}
            ></i>{" "}
            {titleText}
          </h2>
          <span></span>
        </div>

        <div className="form-card-form-appointment">
          <h3 style={{ marginBottom: "1.5rem", textAlign: "center" }}>
            {isEditMode ? `Editando: ${formData.name}` : "Nuevo servicio"}
          </h3>

          {error && (
            <div className="message-box-form-appointment message-error-form-appointment">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="form-group-form-appointment">
              <label htmlFor="name">Nombre del Servicio *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={saving || deleting}
                placeholder="Ej: Consulta inicial, Terapia física..."
                className={formErrors.name ? "input-error" : ""}
              />
              {formErrors.name && (
                <span className="field-error">{formErrors.name}</span>
              )}
            </div>

            {/* <div className="form-group-form-appointment">
              <label htmlFor="service_type">Tipo de Servicio (Opcional)</label>
              <select
                id="service_type"
                name="service_type"
                value={formData.service_type}
                onChange={handleChange}
                disabled={saving || deleting || loadingServiceTypes}
              >
                <option value="">Sin tipo de servicio</option>
                {serviceTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              {loadingServiceTypes && (
                <div
                  style={{
                    marginTop: "var(--space-xs)",
                    fontSize: "0.875rem",
                    color: "var(--color-secondary)",
                  }}
                >
                  <i className="fas fa-spinner fa-spin"></i> Cargando tipos de
                  servicio...
                </div>
              )}
            </div> */}

            <div className="grid-half-form-appointment">
              <div className="form-group-form-appointment">
                <label htmlFor="duration_minutes">Duración (minutos) *</label>
                <input
                  type="number"
                  id="duration_minutes"
                  name="duration_minutes"
                  value={formData.duration_minutes}
                  onChange={handleChange}
                  required
                  disabled={saving || deleting}
                  placeholder="Ej: 30, 60, 90..."
                  min="1"
                  max="480"
                  className={formErrors.duration_minutes ? "input-error" : ""}
                />
                {formErrors.duration_minutes && (
                  <span className="field-error">
                    {formErrors.duration_minutes}
                  </span>
                )}
                {formData.duration_minutes && (
                  <div
                    style={{
                      marginTop: "var(--space-xs)",
                      fontSize: "0.875rem",
                      color: "var(--color-secondary)",
                    }}
                  >
                    Duración: {formatDuration(formData.duration_minutes)}
                  </div>
                )}
              </div>

              <div className="form-group-form-appointment">
                <label htmlFor="price">Precio (MX) *</label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  disabled={saving || deleting}
                  placeholder="Ej: 50.00"
                  min="0"
                  step="0.01"
                  className={formErrors.price ? "input-error" : ""}
                />
                {formErrors.price && (
                  <span className="field-error">{formErrors.price}</span>
                )}
                {formData.price && (
                  <div
                    style={{
                      marginTop: "var(--space-xs)",
                      fontSize: "0.875rem",
                      color: "var(--color-secondary)",
                    }}
                  >
                    Precio: {parseFloat(formData.price).toFixed(2)}MX
                  </div>
                )}
              </div>
            </div>

            <div className="form-group-form-appointment">
              <div className="toggle-container-form-appointment">
                <label
                  className="toggle-label-form-appointment"
                  htmlFor="status"
                >
                  Estado del servicio
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <label className="toggle-switch-form-appointment">
                    <input
                      type="checkbox"
                      id="status"
                      name="status"
                      checked={formData.status}
                      onChange={handleChange}
                      disabled={saving || deleting}
                    />
                    <span className="toggle-slider-form-appointment"></span>
                  </label>
                  <span
                    style={{
                      fontSize: "0.875rem",
                      color: "var(--neutral-300)",
                    }}
                  >
                    {formData.status ? "Activo" : "Inactivo"}
                  </span>
                </div>
              </div>
            </div>

            <div className="form-group-form-appointment">
              <label htmlFor="description">Descripción del Servicio</label>
              <textarea
                id="description"
                name="description"
                rows={4}
                placeholder="Ej: Consulta inicial para evaluar el estado del paciente y establecer un plan de tratamiento personalizado..."
                value={formData.description}
                onChange={handleChange}
                disabled={saving || deleting}
              ></textarea>
            </div>

            <hr
              style={{
                borderColor: "var(--neutral-200)",
                margin: "var(--space-md) 0",
              }}
            />

            <div className="action-footer-form-appointment">
              {isEditMode && (
                <div>
                  <button
                    type="button"
                    className="btn--delete-form-appointment"
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={saving || deleting}
                  >
                    <i className="fas fa-trash-alt"></i>
                    {deleting ? "Eliminando..." : "Eliminar Servicio"}
                  </button>
                  <DeleteModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={handleDeleteConfirm}
                    title="¿Eliminar servicio?"
                    description="Esto eliminará el servicio de forma permanente."
                    confirmText="Eliminar servicio"
                    cancelText="Mantener servicio"
                  />
                </div>
              )}
              <button
                type="submit"
                className={`btn btn--primary btn--large btn--save-form-appointment ${
                  !isEditMode ? "btn--full-width-in-create" : ""
                }`}
                disabled={saving || deleting || loadingServiceTypes}
              >
                <i className="fas fa-save"></i>{" "}
                {saving
                  ? isEditMode
                    ? "Guardando..."
                    : "Creando..."
                  : isEditMode
                    ? "Guardar Cambios"
                    : "Crear Servicio"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ServiceForm;
